package com.tregz.polyglot.ai

import dev.shreyaspatil.ai.client.generativeai.type.content
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

interface PolyglotTranslator {
    suspend fun translate(strings: Map<String, String>, targetLanguage: String): Map<String, String>?

    companion object {
        val default: PolyglotTranslator by lazy { PolyglotGeminiTranslator() }
    }
}

class PolyglotGeminiTranslator : PolyglotTranslator {
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    override suspend fun translate(
        strings: Map<String, String>,
        targetLanguage: String
    ): Map<String, String>? {
        if (strings.isEmpty()) return emptyMap()
        if (Gemini.flash == null) {
            println("[Polyglot] Gemini.flash is null! Check Secrets.GEMINI_API_KEY")
            return null
        }

        val merged = LinkedHashMap<String, String>()
        for (chunk in strings.entries.chunked(CHUNK_SIZE)) {
            val part = chunk.associate { it.key to it.value }
            merged += translateChunk(part, targetLanguage) ?: continue
        }
        val valid = PolyglotValidator.sanitize(strings, merged)
        if (!PolyglotValidator.isAcceptable(strings, valid)) {
            println("[Polyglot] Rejected translation for '$targetLanguage' (${valid.size}/${strings.size} valid)")
            return null
        }
        return valid
    }

    private suspend fun translateChunk(
        strings: Map<String, String>,
        targetLanguage: String
    ): Map<String, String>? {
        val flash = Gemini.flash ?: return null
        val prompt = buildString {
            append("Translate the values of the following JSON key-value map into language code '$targetLanguage'. ")
            append("Do not modify the keys. Keep placeholders, symbols, and formatting intact. ")
            append("Return ONLY a valid JSON object starting with '{' and ending with '}':\n")
            append(json.encodeToString(JsonObject.serializer(), JsonObject(strings.mapValues { JsonPrimitive(it.value) })))
        }
        return try {
            val response = withTimeoutOrNull(TIMEOUT_MS) { flash.generateContent(content { text(prompt) }) }
            if (response == null) {
                println("[Polyglot] Gemini request timed out")
                return null
            }
            val raw = response.text ?: return null
            val jsonStart = raw.indexOf('{')
            val jsonEnd = raw.lastIndexOf('}')
            if (jsonStart !in 0 until jsonEnd) return null
            val element = json.parseToJsonElement(raw.substring(jsonStart, jsonEnd + 1)) as? JsonObject
                ?: return null
            val result = LinkedHashMap<String, String>()
            for ((key, value) in element) {
                if (value is JsonPrimitive && value.isString) result[key] = value.content
            }
            result
        } catch (ex: CancellationException) {
            throw ex
        } catch (ex: Throwable) {
            println("[Polyglot] Gemini generation error: ${ex.message}")
            null
        }
    }

    private companion object {
        const val CHUNK_SIZE = 50
        const val TIMEOUT_MS = 60_000L
    }
}
